import type { IconType } from 'react-icons';
import {
  FaFacebook,
  FaInstagram,
  FaXTwitter,
  FaLinkedin,
  FaTiktok,
  FaYoutube,
  FaPinterest,
  FaThreads,
  FaBluesky,
} from 'react-icons/fa6';

export const PLATFORMS = [
  'facebook',
  'instagram',
  'twitter',
  'linkedin',
  'tiktok',
  'tiktok_business',
  'youtube',
  'pinterest',
  'threads',
  'bluesky',
] as const;

export type Platform = (typeof PLATFORMS)[number];

/**
 * Caption character limits per platform.
 *
 * `standard` is the default limit for any account on that network.
 * `premium` is the upgraded limit when the account is marked as having a
 * premium / paid subscription on the network (currently only relevant for X).
 *
 * Limits not enforced by the network are intentionally omitted (`null`).
 *
 * X Premium allows 25 000 characters in the app, but Post For Me cuts what it
 * publishes to 2 200 for premium accounts, so that's our limit.
 */
export const PLATFORM_CHAR_LIMITS: Record<
  Platform,
  { standard: number | null; premium: number | null }
> = {
  facebook:        { standard: 63206, premium: null },
  instagram:       { standard: 2200,  premium: null },
  twitter:         { standard: 280,   premium: 2200 },
  linkedin:        { standard: 3000,  premium: null },
  tiktok:          { standard: 2200,  premium: null },
  tiktok_business: { standard: 2200,  premium: null },
  youtube:         { standard: 5000,  premium: null },
  pinterest:       { standard: 500,   premium: null },
  threads:         { standard: 500,   premium: null },
  bluesky:         { standard: 300,   premium: null },
};

/** Effective character limit for an account, taking premium status into account. */
export function effectiveCharLimit(platform: Platform, isPremium: boolean): number | null {
  const lim = PLATFORM_CHAR_LIMITS[platform];
  if (!lim) return null;
  if (isPremium && lim.premium != null) return lim.premium;
  return lim.standard;
}

export const PLATFORM_META: Record<
  Platform,
  { label: string; icon: IconType; color: string }
> = {
  facebook: { label: 'Facebook', icon: FaFacebook, color: '#1877F2' },
  instagram: { label: 'Instagram', icon: FaInstagram, color: '#E4405F' },
  twitter: { label: 'X (Twitter)', icon: FaXTwitter, color: '#0F1419' },
  linkedin: { label: 'LinkedIn', icon: FaLinkedin, color: '#0A66C2' },
  tiktok: { label: 'TikTok', icon: FaTiktok, color: '#000000' },
  tiktok_business: { label: 'TikTok Business', icon: FaTiktok, color: '#1F1F1F' },
  youtube: { label: 'YouTube', icon: FaYoutube, color: '#FF0000' },
  pinterest: { label: 'Pinterest', icon: FaPinterest, color: '#BD081C' },
  threads: { label: 'Threads', icon: FaThreads, color: '#000000' },
  bluesky: { label: 'Bluesky', icon: FaBluesky, color: '#0085FF' },
};
